import { useMemo } from 'react';
import { Paper, PaperProps, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ScheduledActionAdminFragmentFragment } from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { ActionIcon } from '../../../components/invoice/ScheduledActionIcon';
import { BooleanIndicator } from '../../../components/table/BooleanIndicator';
import { renderCellOrDefault } from '../../../components/table/renderCellOrDefault';
import { Table } from '../../../components/table/Table';
import { dateGridColDef } from '../../../components/table/utils/columnHeader';

const TRANS_NS = 'page-admin-my-tasks';

type Props = {
  scheduledActions?: ScheduledActionAdminFragmentFragment[];
  isLoading?: boolean;
  isError?: boolean;
  transNs?: string;
  keyPrefix?: string;
} & PaperProps;

export const ScheduledActionsTable = ({
  scheduledActions,
  isError,
  isLoading,
  sx,
  transNs = TRANS_NS,
  keyPrefix,
  ...rest
}: Props) => {
  const { t } = useTranslation(transNs, { keyPrefix });
  return (
    <Paper sx={{ p: { md: 4, xs: 2 }, position: 'relative', minHeight: 270, ...sx }} {...rest}>
      {isLoading ? <FullSizeLoader /> : null}
      {isError ? (
        <ExpandedBox sx={{ backdropFilter: 'blur(2px)', zIndex: 10 }}>
          <Typography>{t('query.error')}</Typography>
        </ExpandedBox>
      ) : null}
      <Typography mb={4} variant={'h2'}>
        {t('scheduledActions.all')}
      </Typography>
      {scheduledActions ? <InvoiceScheduledActionsTable scheduledActions={scheduledActions} /> : null}
    </Paper>
  );
};

const InvoiceScheduledActionsTable = ({
  scheduledActions,
}: {
  scheduledActions: ScheduledActionAdminFragmentFragment[];
}) => {
  const { t } = useTranslation(TRANS_NS);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'plannedAt',
        headerName: t('table.plannedAt') as string,
        minWidth: 130,
        ...dateGridColDef,
      },
      {
        field: 'canceledAt',
        headerName: t('table.canceledAt') as string,
        minWidth: 130,
        ...dateGridColDef,
      },
      {
        field: 'action',
        headerName: t('table.action') as string,
        minWidth: 40,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction={'row'} gap={1} alignItems={'center'}>
            <ActionIcon action={params.value} color={'action'} fontSize={'small'} />
          </Stack>
        ),
      },
      {
        field: 'completed',
        headerName: t('table.completed') as string,
        minWidth: 40,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <BooleanIndicator value={params.value} />,
      },
      {
        field: 'success',
        headerName: t('table.success') as string,
        minWidth: 40,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <BooleanIndicator value={params.value} />,
      },
      {
        field: 'internalNote',
        headerName: t('table.internalNote') as string,
        flex: 1,
        minWidth: 120,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'note',
        headerName: t('table.note') as string,
        flex: 1,
        minWidth: 120,
        renderCell: renderCellOrDefault,
      },
    ],
    [t],
  );

  const rows = useMemo(
    () =>
      scheduledActions
        .sort((a, b) => {
          if (a.scheduledAt < b.scheduledAt) return 1;
          return -1;
        })
        .map((task) => {
          return {
            id: task.id,
            plannedAt: task.scheduledAt,
            canceledAt: task.cancelledAt,
            action: task.action,
            completed: !!task.doneAt,
            success: task.success,
            internalNote: task.internalNote,
            note: task.note,
            invoiceId: task.invoiceId,
          };
        }) ?? [],
    [scheduledActions],
  );

  return (
    <Table
      id={'admin-scheduled-actions'}
      autoHeight={true}
      getRowHeight={() => 'auto'}
      columns={columns}
      rows={rows}
      density={'compact'}
      sx={{
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
          py: '22px',
        },
      }}
    />
  );
};
